import {FormEventHandler, useCallback, useRef} from 'react'
import {FieldValues, SubmitErrorHandler} from 'react-hook-form'
import {
  reportSubmissionValidationFailure,
  SUBMISSION_VALIDATION_FIELD_CODES,
  SubmissionValidationFieldCode,
} from '../../../api'
import {createLocalApplicationPersonId} from '../../common/applicationPersonUtils'

const isSubmissionField = (field: string): field is SubmissionValidationFieldCode =>
  SUBMISSION_VALIDATION_FIELD_CODES.some((knownField) => knownField === field)

export const useSubmissionValidationReporting = <T extends FieldValues>(sessionId: string) => {
  const nativeInvalidFields = useRef(new Set<SubmissionValidationFieldCode>())
  const nativeReportQueued = useRef(false)

  const reportFailure = useCallback(
    (fieldCodes: SubmissionValidationFieldCode[]) => {
      if (!sessionId || fieldCodes.length === 0) return

      // One record describes the failed Send attempt, not each invalid field.
      // Do not retry here or report API failures already recorded by the server.
      void reportSubmissionValidationFailure({
        sessionId,
        attemptId: createLocalApplicationPersonId('submit'),
        fieldCodes,
      }).catch(() => {
        console.warn('Unable to record submission validation failure.')
      })
    },
    [sessionId]
  )

  const onInvalidSubmit = useCallback<SubmitErrorHandler<T>>(
    (errors) => reportFailure(Object.keys(errors).filter(isSubmissionField)),
    [reportFailure]
  )

  const onNativeInvalid = useCallback<FormEventHandler<HTMLFormElement>>(
    (event) => {
      const control = event.target
      if (!(control instanceof HTMLInputElement) || !isSubmissionField(control.name)) return

      nativeInvalidFields.current.add(control.name)
      if (nativeReportQueued.current) return

      // Native email validation can stop submit before React Hook Form runs.
      // Group the browser's invalid events from the same validation pass.
      nativeReportQueued.current = true
      queueMicrotask(() => {
        const fieldCodes = Array.from(nativeInvalidFields.current)
        nativeInvalidFields.current.clear()
        nativeReportQueued.current = false
        reportFailure(fieldCodes)
      })
    },
    [reportFailure]
  )

  return {onInvalidSubmit, onNativeInvalid}
}
